import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PlayArrow } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { ExpandCircleRight } from 'assets/svg/ExpandCircleRight';
import { ICON_SIZE, ICON_COLOR, useMenuItems, MenuType } from 'commons/menu';
import { PATHS } from 'commons/paths';
import { cvtClassName } from 'commons/utils';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { LogoutDialog } from 'organisms/LogoutDialog';

const FONT_COLOR = 'var(--c-nav-text)';

export const MypageMenuBar = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const menuItems = useMenuItems();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = (buttonId: string) => anchorEl != null && anchorEl.id === buttonId;

  const menuItemClassName = (linkTo: string) => {
    const classNames = [
      'inline-flex',
      'items-center',
      'hover:bg-[--c-nav-text]',
      'hover:text-[--c-nav-background]',
      'rounded-full',
      'focus:outline-none',
      'focus:bg-[--c-nav-text]',
      'focus:text-[--c-nav-background]',
    ];
    if (linkTo === location.pathname) {
      classNames.push('underline');
      classNames.push('cursor-default');
      classNames.push('text-[--c-nav-text]');
      classNames.push('hover:bg-[--c-nav-background]');
      classNames.push('hover:text-[--c-nav-text]');
    } else {
      classNames.push('text-theme-dark-blue');
    }
    return classNames;
  };

  const theme = createTheme({
    components: {
      MuiListItem: {
        styleOverrides: {
          root: {
            color: FONT_COLOR,
            width: 'auto',
            '&:hover': {
              backgroundColor: 'unset',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            padding: '0 0.5rem',
            borderRadius: '9999px',
            transition: 'none',
            '&:hover': {
              backgroundColor: 'var(--c-nav-text)',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: FONT_COLOR,
            '&:hover': {
              backgroundColor: 'unset',
            },
          },
        },
      },
    },
  });

  const dispComponent = (item: MenuType) => {
    return (
      <>
        <ExpandCircleRight
          color={ICON_COLOR}
          style={{ width: ICON_SIZE, height: ICON_SIZE }}
        ></ExpandCircleRight>
        <span className="ml-1 text-clamp_s whitespace-nowrap">{item.title}</span>
      </>
    );
  };

  const linkComponent = (item: MenuType) => {
    const dispComp = dispComponent(item);
    return (
      <>
        {(() => {
          if (item.path === PATHS.NONE) {
            return (
              <ListItemButton
                id={item.key}
                onClick={(e) => handleClick(e)}
                sx={{
                  '&:focus': {
                    backgroundColor: 'var(--c-nav-text)',
                    color: 'var(--c-nav-background)',
                  },
                }}
                className="hover:text-[--c-nav-background]"
                component="div"
              >
                {dispComp}
              </ListItemButton>
            );
          } else {
            return (
              <Link to={item.path} className={cvtClassName(menuItemClassName(item.path))}>
                {dispComp}
              </Link>
            );
          }
        })()}
      </>
    );
  };

  const subMenuComponent = (item: MenuType) => {
    if (item.subMenuList == null) return <></>;
    return (
      <>
        <Menu
          anchorEl={anchorEl}
          open={isOpen(item.key)}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                boxShadow: 'none',
                border: 'none',
                background: 'var(--c-nav-background)',
              },
            },
          }}
        >
          {(() => {
            const items = [];
            for (const subMenu of item.subMenuList) {
              items.push(
                <MenuItem
                  key={subMenu.key}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.querySelector('a')?.click();
                    }
                  }}
                >
                  <Link
                    to={subMenu.path}
                    className={cvtClassName(menuItemClassName(subMenu.path))}
                    tabIndex={-1}
                  >
                    <PlayArrow sx={{ fontSize: ICON_SIZE }} />
                    <span className="ml-1 text-clamp_s">{subMenu.title}</span>
                  </Link>
                </MenuItem>
              );
            }
            return items;
          })()}
        </Menu>
      </>
    );
  };

  const { t: tCommons } = useTranslationWithLang('commons');

  return (
    <>
      <ThemeProvider theme={theme}>
        <header>
          <div className="h-full flex flex-col justify-center items-center w-full py-6">
            <img
              src="/logo_spotu.png"
              alt="logo"
              title="logo"
              aria-label="logo"
              style={{ width: '350px', height: 'auto' }}
            ></img>
          </div>
          <nav className="flex justify-center w-full">
            <List component="ul" className="flex py-8 items-center" disablePadding>
              {(() => {
                const list = [];
                for (const item of menuItems) {
                  if (item.mobileOnly) continue;
                  list.push(
                    <ListItem key={item.key} disablePadding className="p-1">
                      {linkComponent(item)}
                      {subMenuComponent(item)}
                    </ListItem>
                  );
                }
                list.push(
                  <ListItem key="logout" disablePadding className="p-1">
                    <ListItemButton
                      id="logout"
                      onClick={() => setOpenLogoutDialog(true)}
                      className="hover:text-[--c-nav-background] rounded-full"
                      sx={{
                        '&:focus': {
                          backgroundColor: 'var(--c-nav-text)',
                          color: 'var(--c-nav-background)',
                        },
                      }}
                    >
                      <ExpandCircleRight
                        color={ICON_COLOR}
                        style={{ width: ICON_SIZE, height: ICON_SIZE }}
                      />
                      <span className="ml-1 text-clamp_s">{tCommons('TU000_008')}</span>
                    </ListItemButton>
                  </ListItem>
                );
                return list;
              })()}
            </List>
          </nav>
        </header>
      </ThemeProvider>
      <LogoutDialog open={openLogoutDialog} setOpen={setOpenLogoutDialog} />
    </>
  );
};
